import { inject, makeEnvironmentProviders } from '@angular/core';
import { authSessionEvents, AuthSessionStore } from '@indutrust/auth-data';
import {
  INDUTRUST_BLUE_PALETTE,
  INDUTRUST_INSTRUMENT_PALETTE,
} from '@indutrust/indutrust-assets';
import {
  ELECTRIC_STATUS_PALETTES,
  THEME_BRAND_PALETTES,
  THEME_BRAND_TOKENS,
} from '@indutrust/shared-data';
import { TOPBAR_HELP_ITEMS_FACTORY } from '@indutrust/shared-feature';
import {
  APP_BRAND_CONFIG,
  APP_SESSION,
  type AppSession,
  SIDE_MENU_CATEGORIES_BY_ROLE,
} from '@indutrust/shared-model';
import { injectDispatch } from '@ngrx/signals/events';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { INDUTRUST_BRAND_CONFIG } from './indutrust.config';
import { INDUTRUST_SIDE_MENU_BY_ROLE } from './side-menu-config';
import { buildIndutrustHelpMenuItems } from './topbar-help-items';
/**
 * Returns Angular environment providers that bootstrap the Indutrust brand layer:
 *
 * - {@link APP_BRAND_CONFIG} — injects {@link INDUTRUST_BRAND_CONFIG} app-wide
 * - {@link APP_SESSION} — adapts {@link AuthSessionStore} + {@link authSessionEvents}
 *   to the brand-agnostic session abstraction consumed by shared libs
 * - {@link SIDE_MENU_CATEGORIES_BY_ROLE} — Indutrust side-menu categories
 * - {@link TOPBAR_HELP_ITEMS_FACTORY} — Indutrust-specific help-menu items
 * - {@link THEME_BRAND_PALETTES} / {@link THEME_BRAND_TOKENS} — authored
 *   action / instrument ramps for {@link ThemeStore}
 * - `providePrimeNG` — initialises PrimeNG with the Indutrust-branded Aura preset
 *
 * Call this once inside `getAppConfig()` in `apps/indutrust-front-v2/src/app/app.config.ts`.
 *
 * @param options Brand-layer options supplied by the host application.
 * @param options.licenseKey The PrimeUI licence token from the app's
 *   `environment.primeNgLicenseKey`. PrimeNG 22 verifies it on bootstrap and
 *   paints a shadow-DOM banner on every page when verification fails, so this
 *   must be set in every deployed environment. Passed in rather than injected
 *   because `providePrimeNG` needs its config synchronously, outside DI.
 * @example
 * ```typescript
 * export const getAppConfig = (): ApplicationConfig => ({
 *   providers: [
 *     provideIndutrustBrandConfig({
 *       licenseKey: environment.primeNgLicenseKey,
 *     }),
 *     // … other providers
 *   ],
 * });
 * ```
 */
export function provideIndutrustBrandConfig(options: { licenseKey?: string }) {
  return makeEnvironmentProviders([
    { provide: APP_BRAND_CONFIG, useValue: INDUTRUST_BRAND_CONFIG },
    {
      provide: APP_SESSION,
      useFactory: (): AppSession => {
        const store = inject(AuthSessionStore);
        const dispatch = injectDispatch(authSessionEvents);
        return {
          user: store.user,
          company: store.company,
          isAuthenticated: store.isAuthenticated,
          teamRole: store.teamRole,
          logout: () => dispatch.logoutRequested(),
        };
      },
    },
    {
      provide: SIDE_MENU_CATEGORIES_BY_ROLE,
      useValue: INDUTRUST_SIDE_MENU_BY_ROLE,
    },
    {
      provide: TOPBAR_HELP_ITEMS_FACTORY,
      useValue: buildIndutrustHelpMenuItems,
    },
    {
      provide: THEME_BRAND_PALETTES,
      useValue: {
        indutrust: INDUTRUST_BLUE_PALETTE,
      },
    },
    {
      provide: THEME_BRAND_TOKENS,
      useValue: {
        primaryName: 'indutrust',
        primary: INDUTRUST_BLUE_PALETTE,
        instrument: INDUTRUST_INSTRUMENT_PALETTE,
      },
    },
    providePrimeNG({
      license: options.licenseKey,
      theme: {
        preset: definePreset(Aura, {
          primitive: ELECTRIC_STATUS_PALETTES,
          semantic: { primary: INDUTRUST_BLUE_PALETTE },
        }),
        options: { darkModeSelector: '.app-dark' },
      },
      ripple: false,
    }),
  ]);
}
